import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getComicsByCategory, getCategoryBySlug, categories } from '@/mocks/comics';
import { useState } from 'react';

const sortOptions = [
  { key: 'latest', label: '最新更新' },
  { key: 'popular', label: '最受欢迎' },
  { key: 'rating', label: '评分最高' },
];

const statusFilters = [
  { key: 'all', label: '全部' },
  { key: '连载中', label: '连载中' },
  { key: '已完结', label: '已完结' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const comics = getComicsByCategory(slug || '');
  const [sortBy, setSortBy] = useState('latest');
  const [statusFilter, setStatusFilter] = useState('all');

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollToTop />
        <div className="pt-20 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  let filtered = statusFilter === 'all' ? comics : comics.filter(c => c.status === statusFilter);

  if (sortBy === 'popular') {
    filtered = [...filtered].sort((a, b) => b.views - a.views);
  } else if (sortBy === 'rating') {
    filtered = [...filtered].sort((a, b) => b.rating - a.rating);
  } else {
    filtered = [...filtered].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - h漫画`,
            description: category.description,
            url: `https://hmanga.com/category/${category.slug}`,
            isPartOf: { '@type': 'WebSite', name: 'h漫画', url: 'https://hmanga.com' },
          }),
        }}
      />

      <div className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: category.name },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center text-primary-600 flex-shrink-0">
                <i className={`${category.icon} text-xl w-6 h-6 flex items-center justify-center`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {comics.length} 部作品
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500 flex-shrink-0">状态：</span>
                <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                  {statusFilters.map((f) => (
                    <button
                      key={f.key}
                      onClick={() => setStatusFilter(f.key)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        statusFilter === f.key
                          ? 'bg-primary-500 text-white'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 flex-shrink-0">排序：</span>
                <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        sortBy === opt.key
                          ? 'bg-accent-500 text-white'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filtered.map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="medium" />
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">暂无符合条件的作品</p>
              </div>
            )}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">其他分类</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {categories
                .filter(c => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center text-secondary-600 flex-shrink-0">
                      <i className={`${cat.icon} w-5 h-5 flex items-center justify-center`}></i>
                    </div>
                    <div>
                      <span className="font-medium text-foreground-900 text-sm">{cat.name}</span>
                      <p className="text-xs text-foreground-500">{cat.count} 部作品</p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}